import { useQuery } from '@tanstack/react-query';
import { MaintenanceExecutionService } from '~/app/services/maintenance-execution/maintenance-execution.service';
import { asOptionalString } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { pickCurativeIncidentFields } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils/curative-incident-fields';
import type {
  KanbanTicketDetailView,
  KanbanTicketReportView,
  KanbanWorkOrderDetailView,
} from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { WorkOrderStatus } from '@evimo/types/enums/maintenance-execution';

export const workOrderDetailQueryKey = (
  workOrderId: string,
  organizationId: string,
) =>
  [
    'maintenance-execution',
    'work-order',
    'v6-incident-resolve',
    workOrderId,
    organizationId,
  ] as const;

function mapReport(
  report:
    | {
        executionResult?: string | null;
        summary?: string | null;
        workPerformed?: string | null;
        recommendation?: string | null;
        technicianComment?: string | null;
        faultCategory?: string | null;
        faultDescription?: string | null;
        confirmedSerialNumber?: string | null;
        confirmedAssetIdentifier?: string | null;
        pauseDurationMinutes?: number | null;
        arrivedAt?: string | Date | null;
      }
    | null
    | undefined,
): KanbanTicketReportView | null {
  if (report == null) return null;
  const arrivedRaw = report.arrivedAt;
  const arrivedAt =
    arrivedRaw == null
      ? null
      : arrivedRaw instanceof Date
        ? arrivedRaw
        : new Date(arrivedRaw);
  return {
    executionResult: asOptionalString(report.executionResult) ?? null,
    summary: asOptionalString(report.summary) ?? null,
    workPerformed: asOptionalString(report.workPerformed) ?? null,
    recommendation: asOptionalString(report.recommendation) ?? null,
    technicianComment: asOptionalString(report.technicianComment) ?? null,
    faultCategory: asOptionalString(report.faultCategory) ?? null,
    faultDescription: asOptionalString(report.faultDescription) ?? null,
    confirmedSerialNumber:
      asOptionalString(report.confirmedSerialNumber) ?? null,
    confirmedAssetIdentifier:
      asOptionalString(report.confirmedAssetIdentifier) ?? null,
    pauseDurationMinutes:
      typeof report.pauseDurationMinutes === 'number'
        ? report.pauseDurationMinutes
        : null,
    arrivedAt:
      arrivedAt && !Number.isNaN(arrivedAt.getTime()) ? arrivedAt : null,
  };
}

export function useWorkOrderDetail(args: {
  workOrderId: string | null;
  organizationId: string;
  siteNameById: Map<string, string>;
  technicianNameById: Map<string, string>;
  unknownSiteLabel: string;
  unknownTechnicianLabel: string;
}) {
  const {
    workOrderId,
    organizationId,
    siteNameById,
    technicianNameById,
    unknownSiteLabel,
    unknownTechnicianLabel,
  } = args;

  return useQuery({
    queryKey: workOrderDetailQueryKey(workOrderId ?? '', organizationId),
    queryFn: async (): Promise<KanbanWorkOrderDetailView> => {
      const detail = await MaintenanceExecutionService.getWorkOrder(
        workOrderId!,
        organizationId,
      );
      const technicianId = asOptionalString(detail.assignedTechnicianId);
      const tickets: KanbanTicketDetailView[] = detail.tickets.map((ticket) => {
        const report = mapReport(
          ticket.report as Parameters<typeof mapReport>[0],
        );
        return {
          id: ticket.id,
          membershipId: ticket.membershipId,
          code: ticket.code,
          type: ticket.type,
          status: ticket.status,
          needsFollowUp: ticket.needsFollowUp,
          priority: ticket.priority,
          title: ticket.title,
          dueAt: ticket.dueAt ?? null,
          taskId: asOptionalString(ticket.taskId),
          preventivePlanId: asOptionalString(ticket.preventivePlanId),
          planCode: asOptionalString(ticket.planCode),
          planName: asOptionalString(ticket.planName),
          assetId: asOptionalString(ticket.assetId),
          assetName: asOptionalString(ticket.assetName),
          assetSerialNumber: asOptionalString(ticket.assetSerialNumber),
          lastExecutionResult:
            asOptionalString(ticket.lastExecutionResult) ??
            report?.executionResult ??
            null,
          ...pickCurativeIncidentFields(
            ticket as {
              incidentId?: unknown;
              incidentCode?: unknown;
              incidentTitle?: unknown;
            },
          ),
          subtaskNames: ticket.subtaskNames ?? [],
          report,
        };
      });
      const highestPriority = tickets.some((t) => t.priority === 'HIGH')
        ? 'HIGH'
        : tickets.some((t) => t.priority === 'MEDIUM')
          ? 'MEDIUM'
          : tickets.some((t) => t.priority === 'LOW')
            ? 'LOW'
            : 'MEDIUM';

      const createdAt =
        detail.createdAt instanceof Date
          ? detail.createdAt
          : new Date(detail.createdAt);
      const updatedAt =
        detail.updatedAt instanceof Date
          ? detail.updatedAt
          : new Date(detail.updatedAt);

      return {
        id: detail.id,
        code: detail.code,
        status: detail.status as WorkOrderStatus,
        siteId: detail.siteId,
        siteName: siteNameById.get(detail.siteId) ?? unknownSiteLabel,
        externalIdentifier: asOptionalString(
          (detail as { externalIdentifier?: string | null }).externalIdentifier,
        ),
        assignedTechnicianId: technicianId,
        assignedTechnicianName: technicianId
          ? (technicianNameById.get(technicianId) ?? unknownTechnicianLabel)
          : null,
        createdAt: Number.isNaN(createdAt.getTime()) ? new Date(0) : createdAt,
        updatedAt: Number.isNaN(updatedAt.getTime()) ? new Date(0) : updatedAt,
        tickets,
        highestPriority,
        scheduledFor: detail.scheduledFor ?? null,
        startedAt: detail.startedAt ?? null,
        completedAt: detail.completedAt ?? null,
        onHoldReason: asOptionalString(detail.onHoldReason),
      };
    },
    enabled: Boolean(workOrderId && organizationId),
  });
}
